import { useState, useMemo } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const ProjectsSection = styled.div`
  margin-top: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const FiltersContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: #333;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #0000FF;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0;
  padding: 15px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const StatsText = styled.span`
  font-weight: 600;
  color: #333;
  font-size: 16px;
`;

const ProjectsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.thead`
  background: #0000FF;
  color: white;
  
  th {
    padding: 15px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid #e0e0e0;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: #f5f5f5;
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  td {
    padding: 15px;
    font-size: 14px;
    vertical-align: top;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  
  ${props => {
    switch(props.status) {
      case 'pending':
        return 'background: #ffc107; color: #000;';
      case 'reviewed':
        return 'background: #17a2b8; color: white;';
      case 'approved':
        return 'background: #28a745; color: white;';
      case 'rejected':
        return 'background: #dc3545; color: white;';
      default:
        return 'background: #6c757d; color: white;';
    }
  }}
`;

const CompanyName = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
`;

const ContactInfo = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 3px;
`;

const PieceName = styled.div`
  font-weight: 500;
  color: #333;
`;

const MediaCount = styled.span`
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  color: #495057;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-style: italic;
  background: white;
  border-radius: 8px;
`;

const Milan2026Admin = ({ pieces = [], loading = false }) => {
  const [statusFilter, setStatusFilter] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const [sortBy, setSortBy] = useState("date");

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Nouveau';
      case 'reviewed': return 'Examiné';
      case 'approved': return 'Approuvé';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  const sortPieces = (a, b) => {
    switch(sortBy) {
      case 'date':
        return new Date(b.submissionDate) - new Date(a.submissionDate);
      case 'company':
        const companyA = (a.submissionData.userType === 'designer' 
          ? a.submissionData.designerCompanyName 
          : a.submissionData.fabricantCompanyName).toLowerCase();
        const companyB = (b.submissionData.userType === 'designer' 
          ? b.submissionData.designerCompanyName 
          : b.submissionData.fabricantCompanyName).toLowerCase();
        return companyA.localeCompare(companyB);
      case 'piece':
        return a.name.localeCompare(b.name);
      case 'status':
        return a.submissionStatus.localeCompare(b.submissionStatus);
      default:
        return 0;
    }
  };

  const filteredPieces = useMemo(() => {
    return pieces
      .filter(piece => {
        if (statusFilter && piece.submissionStatus !== statusFilter) return false;
        if (userTypeFilter && piece.submissionData.userType !== userTypeFilter) return false;
        return true;
      })
      .sort(sortPieces);
  }, [pieces, statusFilter, userTypeFilter, sortBy]);

  if (loading) {
    return (
      <ProjectsSection>
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Chargement des données Milan 2026...
        </div>
      </ProjectsSection>
    );
  }

  return (
    <ProjectsSection>
      <h3 style={{ marginBottom: '20px', color: '#333' }}>Milan 2026 - Soumissions de pièces</h3>
      
      <FiltersContainer>
        <FilterGroup>
          <FilterLabel>Statut</FilterLabel>
          <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Tous les statuts</option>
            <option value="pending">Nouveau</option>
            <option value="reviewed">Examiné</option>
            <option value="approved">Approuvé</option>
            <option value="rejected">Rejeté</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Type d'utilisateur</FilterLabel>
          <FilterSelect value={userTypeFilter} onChange={(e) => setUserTypeFilter(e.target.value)}>
            <option value="">Tous</option>
            <option value="designer">Designer</option>
            <option value="fabricant">Fabricant</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Tri</FilterLabel>
          <FilterSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date">Date de soumission</option>
            <option value="company">Nom de l'entreprise</option>
            <option value="piece">Nom de la pièce</option>
            <option value="status">Statut</option>
          </FilterSelect>
        </FilterGroup>
      </FiltersContainer>

      <StatsContainer>
        <StatsText>{filteredPieces.length} pièce(s) trouvée(s)</StatsText>
      </StatsContainer>

      {filteredPieces.length === 0 ? (
        <NoDataMessage>
          {pieces.length === 0 
            ? "Aucune pièce Milan 2026 pour le moment."
            : "Aucune pièce trouvée avec les filtres sélectionnés."
          }
        </NoDataMessage>
      ) : (
        <ProjectsTable>
          <TableHeader>
            <tr>
              <th>Date</th>
              <th>Entreprise / Contact</th>
              <th>Pièce</th>
              <th>Médias</th>
              <th>Statut</th>
            </tr>
          </TableHeader>
          <TableBody>
            {filteredPieces.map((piece) => (
              <tr key={`${piece.submissionId}-${piece.pieceIndex}`}>
                <td>
                  {dayjs(piece.submissionDate).format('DD/MM/YYYY HH:mm')}
                </td>
                
                <td>
                  <CompanyName>
                    {piece.submissionData.userType === 'designer' 
                      ? piece.submissionData.designerCompanyName 
                      : piece.submissionData.fabricantCompanyName}
                  </CompanyName>
                  <ContactInfo>
                    {piece.submissionData.firstName} {piece.submissionData.lastName}
                  </ContactInfo>
                  <ContactInfo>
                    {piece.submissionData.email}
                  </ContactInfo>
                  <StatusBadge status={piece.submissionData.userType}>
                    {piece.submissionData.userType === 'designer' ? 'Designer' : 'Fabricant'}
                  </StatusBadge>
                </td>
                
                <td>
                  <PieceName>{piece.name}</PieceName>
                  {piece.description && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      {piece.description.substring(0, 100)}
                      {piece.description.length > 100 && '...'}
                    </div>
                  )}
                </td>
                
                <td>
                  <MediaCount>
                    {piece.uploadedMedia?.length || 0} média(s)
                  </MediaCount>
                </td>
                
                <td>
                  <StatusBadge status={piece.submissionStatus}>
                    {getStatusText(piece.submissionStatus)}
                  </StatusBadge>
                </td>
              </tr>
            ))}
          </TableBody>
        </ProjectsTable>
      )}
    </ProjectsSection>
  );
};

export default Milan2026Admin;